import React from 'react';
import { Navbar } from '../components/Navbar';
import { StickyFooter } from '../components/StickyFooter';
import { ServiceHero } from '../components/ServiceHero';
import { Link } from 'react-router-dom';
import { ShieldCheckIcon, HeartIcon, AwardIcon, UsersIcon, StarIcon, QuoteIcon, MapPinIcon } from 'lucide-react';
const WORKERS = [{
  name: 'Sarah M.',
  role: 'RN',
  rating: 4.9,
  location: 'Toronto, ON',
  image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&auto=format&fit=crop'
}, {
  name: 'David C.',
  role: 'RPN',
  rating: 4.8,
  location: 'Vancouver, BC',
  image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400&auto=format&fit=crop'
}, {
  name: 'Priya K.',
  role: 'PSW',
  rating: 4.9,
  location: 'Calgary, AB',
  image: 'https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=400&auto=format&fit=crop'
}, {
  name: 'Amara O.',
  role: 'RN',
  rating: 4.7,
  location: 'Ottawa, ON',
  image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&auto=format&fit=crop'
}, {
  name: 'James L.',
  role: 'RPN',
  rating: 4.8,
  location: 'Mississauga, ON',
  image: 'https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=400&auto=format&fit=crop'
}, {
  name: 'Maria G.',
  role: 'PSW',
  rating: 4.9,
  location: 'Edmonton, AB',
  image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&auto=format&fit=crop'
}, {
  name: 'Michael R.',
  role: 'RN',
  rating: 4.8,
  location: 'Hamilton, ON',
  image: 'https://images.unsplash.com/photo-1537368910025-700350fe46c7?w=400&auto=format&fit=crop'
}, {
  name: 'Jessica W.',
  role: 'RPN',
  rating: 4.7,
  location: 'Surrey, BC',
  image: 'https://images.unsplash.com/photo-1551076805-e1869033e561?w=400&auto=format&fit=crop'
}];
const TESTIMONIALS = [{
  quote: 'Care Nomad has transformed how we handle staffing shortages. We went from scrambling for days to filling shifts in minutes. The quality of workers is consistently excellent.',
  author: 'Linda T.',
  role: 'Director of Care',
  facility: 'Sunrise LTC, Toronto',
  initials: 'LT'
}, {
  quote: 'For the first time in my career, I feel valued. I choose my shifts, I get paid fairly, and I know the facilities I work at actually want me there.',
  author: 'Marcus J.',
  role: 'RPN',
  facility: 'Care Nomad Worker, Vancouver',
  initials: 'MJ'
}, {
  quote: "The nurse-led vetting process gives us confidence that every worker who walks through our doors meets our clinical standards. It's a game-changer.",
  author: 'Dr. Patricia H.',
  role: 'Medical Director',
  facility: 'Maple Grove Health Centre, Calgary',
  initials: 'PH'
}];
export function WhyUs() {
  return <div className="min-h-screen w-full bg-white flex flex-col" data-id="element-1121">
      <Navbar data-id="element-1122" />

      <main className="flex-1" data-id="element-1123">
        {/* 1. Hero Section */}
        <ServiceHero badge="Nurse-Owned & Operated" title="Why Choose Care Nomad?" subtitle="Canada's first nurse-led healthcare staffing marketplace — built by healthcare professionals who understand your challenges." imageUrl="https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=1600&auto=format&fit=crop" data-id="element-1124" />

        {/* 2. Value Propositions */}
        <section className="py-20 lg:py-28 bg-white" data-id="element-1125">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1126">
            <div className="text-center mb-16" data-id="element-1127">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-text-dark tracking-tight" data-id="element-1128">
                What Sets Us Apart
              </h2>
              <div className="w-24 h-1.5 bg-accent-teal mx-auto mt-6 rounded-full" data-id="element-1129" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12" data-id="element-1130">
              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100 hover:shadow-float transition-shadow duration-300" data-id="element-1131">
                <div className="w-14 h-14 bg-soft-teal rounded-full flex items-center justify-center mb-6" data-id="element-1132">
                  <ShieldCheckIcon className="w-7 h-7 text-accent-teal" data-id="element-1133" />
                </div>
                <h3 className="text-xl font-bold text-text-dark mb-3" data-id="element-1134">
                  Nurse-Vetted Quality
                </h3>
                <p className="text-text-muted leading-relaxed" data-id="element-1135">
                  Every worker is screened by a registered nurse, not HR
                  generalists. Credentials verified, references checked, and
                  clinical competency assessed before they ever take a shift.
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100 hover:shadow-float transition-shadow duration-300" data-id="element-1136">
                <div className="w-14 h-14 bg-soft-teal rounded-full flex items-center justify-center mb-6" data-id="element-1137">
                  <HeartIcon className="w-7 h-7 text-accent-teal" data-id="element-1138" />
                </div>
                <h3 className="text-xl font-bold text-text-dark mb-3" data-id="element-1139">
                  Worker-First Philosophy
                </h3>
                <p className="text-text-muted leading-relaxed" data-id="element-1140">
                  Healthcare workers choose their shifts, set their rates, and
                  keep more of their earnings. Better pay equals better
                  retention, which ultimately leads to better patient outcomes.
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100 hover:shadow-float transition-shadow duration-300" data-id="element-1141">
                <div className="w-14 h-14 bg-soft-teal rounded-full flex items-center justify-center mb-6" data-id="element-1142">
                  <AwardIcon className="w-7 h-7 text-accent-teal" data-id="element-1143" />
                </div>
                <h3 className="text-xl font-bold text-text-dark mb-3" data-id="element-1144">
                  Regulated & Compliant
                </h3>
                <p className="text-text-muted leading-relaxed" data-id="element-1145">
                  Fully compliant with Ontario LTCHA, BC CCALA, and federal
                  healthcare regulations. All workers carry valid provincial
                  registration and comprehensive liability insurance.
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100 hover:shadow-float transition-shadow duration-300" data-id="element-1146">
                <div className="w-14 h-14 bg-soft-teal rounded-full flex items-center justify-center mb-6" data-id="element-1147">
                  <UsersIcon className="w-7 h-7 text-accent-teal" data-id="element-1148" />
                </div>
                <h3 className="text-xl font-bold text-text-dark mb-3" data-id="element-1149">
                  Community-Driven
                </h3>
                <p className="text-text-muted leading-relaxed" data-id="element-1150">
                  We believe in giving back to the profession that built us. A
                  portion of every transaction funds nursing scholarships and
                  essential mental health support for healthcare workers.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 3. Stats Bar */}
        <section className="bg-medical-blue py-16" data-id="element-1151">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1152">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center divide-x-0 lg:divide-x lg:divide-white/20" data-id="element-1153">
              <div className="px-4" data-id="element-1154">
                <div className="text-4xl sm:text-5xl font-extrabold text-white mb-2" data-id="element-1155">
                  2,400+
                </div>
                <div className="text-soft-teal font-medium text-sm uppercase tracking-wider" data-id="element-1156">
                  Verified Workers
                </div>
              </div>
              <div className="px-4" data-id="element-1157">
                <div className="text-4xl sm:text-5xl font-extrabold text-white mb-2" data-id="element-1158">
                  98%
                </div>
                <div className="text-soft-teal font-medium text-sm uppercase tracking-wider" data-id="element-1159">
                  Fill Rate
                </div>
              </div>
              <div className="px-4" data-id="element-1160">
                <div className="text-4xl sm:text-5xl font-extrabold text-white mb-2" data-id="element-1161">
                  4 min
                </div>
                <div className="text-soft-teal font-medium text-sm uppercase tracking-wider" data-id="element-1162">
                  Avg Match Time
                </div>
              </div>
              <div className="px-4" data-id="element-1163">
                <div className="text-4xl sm:text-5xl font-extrabold text-white mb-2" data-id="element-1164">
                  40%
                </div>
                <div className="text-soft-teal font-medium text-sm uppercase tracking-wider" data-id="element-1165">
                  Cost Savings
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 4. Worker Gallery */}
        <section className="py-20 lg:py-28 bg-soft-teal" data-id="element-1166">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1167">
            <div className="text-center mb-16" data-id="element-1168">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-text-dark tracking-tight mb-4" data-id="element-1169">
                Meet Our Care Professionals
              </h2>
              <p className="text-lg text-text-muted max-w-2xl mx-auto" data-id="element-1170">
                Every member of our team is nurse-vetted, credential-verified,
                and passionate about patient care.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8" data-id="element-1171">
              {WORKERS.map((worker, index) => <div key={index} className="bg-white rounded-2xl shadow-card overflow-hidden group hover:shadow-float transition-all duration-300" data-id="element-1172">
                  <div className="aspect-square overflow-hidden relative" data-id="element-1173">
                    <img src={worker.image} alt={worker.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-1174" />
                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-full text-xs font-bold text-medical-blue shadow-sm" data-id="element-1175">
                      {worker.role}
                    </div>
                  </div>
                  <div className="p-5" data-id="element-1176">
                    <div className="flex items-center justify-between mb-1" data-id="element-1177">
                      <h3 className="font-bold text-text-dark text-lg" data-id="element-1178">
                        {worker.name}
                      </h3>
                      <div className="flex items-center gap-1 bg-gray-50 px-2 py-0.5 rounded-md" data-id="element-1179">
                        <StarIcon className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" data-id="element-1180" />
                        <span className="text-xs font-bold text-text-dark" data-id="element-1181">
                          {worker.rating}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-text-muted mt-2" data-id="element-1182">
                      <MapPinIcon className="w-4 h-4 text-accent-teal" data-id="element-1183" />
                      {worker.location}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </section>

        {/* 5. Testimonials */}
        <section className="py-20 lg:py-28 bg-white" data-id="element-1184">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1185">
            <div className="text-center mb-16" data-id="element-1186">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-text-dark tracking-tight" data-id="element-1187">
                Trusted by Facilities & Workers Across Canada
              </h2>
              <div className="w-24 h-1.5 bg-accent-teal mx-auto mt-6 rounded-full" data-id="element-1188" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1189">
              {TESTIMONIALS.map((testimonial, index) => <div key={index} className="bg-white rounded-2xl shadow-card border border-gray-100 p-8 flex flex-col h-full hover:shadow-float transition-shadow duration-300" data-id="element-1190">
                  <QuoteIcon className="w-10 h-10 text-accent-teal/20 mb-6" data-id="element-1191" />
                  <p className="text-text-dark italic leading-relaxed flex-1 mb-8 text-lg" data-id="element-1192">
                    "{testimonial.quote}"
                  </p>
                  <div className="border-t border-gray-100 pt-6 flex items-center gap-4" data-id="element-1193">
                    <div className="w-12 h-12 rounded-full bg-soft-teal flex items-center justify-center text-medical-blue font-bold text-lg flex-shrink-0" data-id="element-1194">
                      {testimonial.initials}
                    </div>
                    <div data-id="element-1195">
                      <div className="font-bold text-text-dark" data-id="element-1196">
                        {testimonial.author}
                      </div>
                      <div className="text-sm text-accent-teal font-medium" data-id="element-1197">
                        {testimonial.role}
                      </div>
                      <div className="text-xs text-text-muted mt-0.5" data-id="element-1198">
                        {testimonial.facility}
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </section>

        {/* 6. CTA Section */}
        <section className="bg-medical-blue py-20" data-id="element-1199">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1200">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-6" data-id="element-1201">
              Ready to Experience the Care Nomad Difference?
            </h2>
            <p className="text-xl text-white/90 mb-10" data-id="element-1202">
              Join thousands of facilities and healthcare workers across Canada.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1203">
              <Link to="/contact" className="w-full sm:w-auto px-8 py-4 bg-white text-medical-blue font-bold rounded-xl hover:bg-gray-50 transition-colors shadow-lg" data-id="element-1204">
                Get Care Now
              </Link>
              <Link to="/contact" className="w-full sm:w-auto px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-xl hover:bg-white/10 transition-colors" data-id="element-1205">
                Join as a Worker
              </Link>
            </div>
          </div>
        </section>
      </main>

      <StickyFooter data-id="element-1206" />
    </div>;
}